import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  PlayArrowOutlined as PlayIcon,
  OpenInNewOutlined as OpenIcon,
  BookmarkBorderOutlined as BookmarkIcon,
  BookmarkOutlined as BookmarkedIcon,
  RefreshOutlined as RefreshIcon,
} from '@mui/icons-material';
import { useTheme } from '@emotion/react';
import axios from 'axios';
import { getApiRoute } from 'src/services/Config.js';
import { videoResourcesCache } from 'src/services/VideoResourcesCache.js';

// Verified Khan Academy videos by unit day
const getVerifiedKhanVideos = (unitDay) => {
  const videosByDay = {
    1: [ // Area and Perimeter - Verified Khan Academy
      {
        id: 'ka-area-perimeter-1',
        title: 'Area and perimeter: the basics',
        description: 'Introduction to finding area and perimeter of rectangles.',
        thumbnail: 'https://img.youtube.com/vi/YcFB1A-6iWQ/maxresdefault.jpg',
        videoId: 'YcFB1A-6iWQ',
        duration: '7:23',
        url: 'https://www.youtube.com/watch?v=YcFB1A-6iWQ'
      },
      {
        id: 'ka-area-rectangles',
        title: 'Finding area of rectangles and squares',
        description: 'Learn to calculate area using length times width.',
        thumbnail: 'https://img.youtube.com/vi/VLnFP9y9bBs/maxresdefault.jpg',
        videoId: 'VLnFP9y9bBs',
        duration: '4:12',
        url: 'https://www.youtube.com/watch?v=VLnFP9y9bBs'
      },
      {
        id: 'ka-perimeter-basics',
        title: 'Perimeter of a shape',
        description: 'Understanding perimeter as the distance around a shape.',
        thumbnail: 'https://img.youtube.com/vi/mLaitZrEzZs/maxresdefault.jpg',
        videoId: 'mLaitZrEzZs',
        duration: '3:45',
        url: 'https://www.youtube.com/watch?v=mLaitZrEzZs'
      }
    ],
    2: [ // Decimals and Place Value - Verified Khan Academy  
      {
        id: 'ka-decimal-place-value',
        title: 'Decimal place value',
        description: 'Understanding tenths, hundredths, and thousandths places.',
        thumbnail: 'https://img.youtube.com/vi/x7J_3AtGc0Q/maxresdefault.jpg',
        videoId: 'x7J_3AtGc0Q',
        duration: '7:24',
        url: 'https://www.youtube.com/watch?v=x7J_3AtGc0Q'
      },
      {
        id: 'ka-adding-decimals',
        title: 'Adding decimals',
        description: 'Step-by-step process for adding decimal numbers.',
        thumbnail: 'https://img.youtube.com/vi/dzK7TlsY3fU/maxresdefault.jpg',
        videoId: 'dzK7TlsY3fU',
        duration: '5:18',
        url: 'https://www.youtube.com/watch?v=dzK7TlsY3fU'
      },
      {
        id: 'ka-decimal-expanded-form',
        title: 'Writing decimals in expanded form',
        description: 'Express decimals using place value notation.',
        thumbnail: 'https://img.youtube.com/vi/q85KuhF7x6U/maxresdefault.jpg',
        videoId: 'q85KuhF7x6U',
        duration: '4:32',
        url: 'https://www.youtube.com/watch?v=q85KuhF7x6U'
      }
    ],
    3: [ // Multiplication - Verified Khan Academy
      {
        id: 'ka-multiplication-intro',
        title: 'Introduction to multiplication',
        description: 'Understanding multiplication as repeated addition.',
        thumbnail: 'https://img.youtube.com/vi/dt_jy3hHCpY/maxresdefault.jpg',
        videoId: 'dt_jy3hHCpY',
        duration: '6:42',
        url: 'https://www.youtube.com/watch?v=dt_jy3hHCpY'
      },
      {
        id: 'ka-multi-digit-multiplication',
        title: 'Multi-digit multiplication',
        description: 'Multiplying larger numbers using the standard algorithm.',
        thumbnail: 'https://img.youtube.com/vi/rKr6pz1lqb8/maxresdefault.jpg',
        videoId: 'rKr6pz1lqb8',
        duration: '5:27',
        url: 'https://www.youtube.com/watch?v=rKr6pz1lqb8'
      },
      {
        id: 'ka-area-model-multiplication',
        title: 'Area model multiplication',
        description: 'Using area models to understand multi-digit multiplication.',
        thumbnail: 'https://img.youtube.com/vi/OANJv_nd7Gs/maxresdefault.jpg',
        videoId: 'OANJv_nd7Gs',
        duration: '7:15',
        url: 'https://www.youtube.com/watch?v=OANJv_nd7Gs'
      }
    ],
    4: [ // Visual Patterns for Mathematical Expressions - Verified Khan Academy
      {
        id: 'ka-visual-patterns-1',
        title: 'Introduction to patterns',
        description: 'Recognizing and describing visual patterns in mathematics.',
        thumbnail: 'https://img.youtube.com/vi/8T5acEwfJbw/maxresdefault.jpg',
        videoId: '8T5acEwfJbw',
        duration: '4:18',
        url: 'https://www.youtube.com/watch?v=8T5acEwfJbw'
      },
      {
        id: 'ka-number-patterns',
        title: 'Number patterns',
        description: 'Finding patterns in sequences of numbers.',
        thumbnail: 'https://img.youtube.com/vi/NjKoDGD-rQs/maxresdefault.jpg',
        videoId: 'NjKoDGD-rQs',
        duration: '5:43',
        url: 'https://www.youtube.com/watch?v=NjKoDGD-rQs'
      },
      {
        id: 'ka-growing-patterns',
        title: 'Growing patterns',
        description: 'Understanding how visual patterns grow and change.',
        thumbnail: 'https://img.youtube.com/vi/2SUvWfNJSsM/maxresdefault.jpg',
        videoId: '2SUvWfNJSsM',
        duration: '6:22',
        url: 'https://www.youtube.com/watch?v=2SUvWfNJSsM'
      },
      {
        id: 'ka-pattern-expressions',
        title: 'Writing expressions for patterns',
        description: 'Converting visual patterns into mathematical expressions.',
        thumbnail: 'https://img.youtube.com/vi/vFjvQDqsO48/maxresdefault.jpg',
        videoId: 'vFjvQDqsO48',
        duration: '7:05',
        url: 'https://www.youtube.com/watch?v=vFjvQDqsO48'
      }
    ],
    5: [ // Word Problems for 5th Graders - Verified Khan Academy
      {
        id: 'ka-word-problems-intro',
        title: 'Multi-step word problems',
        description: 'Solving complex word problems with multiple operations.',
        thumbnail: 'https://img.youtube.com/vi/JJLJu8T8-qE/maxresdefault.jpg',
        videoId: 'JJLJu8T8-qE',
        duration: '8:14',
        url: 'https://www.youtube.com/watch?v=JJLJu8T8-qE'
      },
      {
        id: 'ka-fraction-word-problems',
        title: 'Fraction word problems',
        description: 'Solving word problems involving fractions and mixed numbers.',
        thumbnail: 'https://img.youtube.com/vi/YjilQrXzR04/maxresdefault.jpg',
        videoId: 'YjilQrXzR04',
        duration: '6:37',
        url: 'https://www.youtube.com/watch?v=YjilQrXzR04'
      },
      {
        id: 'ka-decimal-word-problems',
        title: 'Decimal word problems',
        description: 'Real-world problems using decimal operations.',
        thumbnail: 'https://img.youtube.com/vi/j3mhkYbznBk/maxresdefault.jpg',
        videoId: 'j3mhkYbznBk',
        duration: '8:32',
        url: 'https://www.youtube.com/watch?v=j3mhkYbznBk'
      },
      {
        id: 'ka-measurement-word-problems',
        title: 'Measurement word problems',
        description: 'Solving problems involving length, weight, and capacity.',
        thumbnail: 'https://img.youtube.com/vi/r-V_RF7nPW8/maxresdefault.jpg',
        videoId: 'r-V_RF7nPW8',
        duration: '5:49',
        url: 'https://www.youtube.com/watch?v=r-V_RF7nPW8'
      },
      {
        id: 'ka-money-word-problems',
        title: 'Money word problems',
        description: 'Word problems involving money, shopping, and financial literacy.',
        thumbnail: 'https://img.youtube.com/vi/lGnKrepKi6E/maxresdefault.jpg',
        videoId: 'lGnKrepKi6E',
        duration: '7:28',
        url: 'https://www.youtube.com/watch?v=lGnKrepKi6E'
      }
    ]
  };

  return videosByDay[unitDay] || [];
};

const VideoCard = ({ video, onBookmark, isBookmarked = false }) => {
  const theme = useTheme();

  const handleVideoClick = () => {
    window.open(video.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.2s ease-in-out',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[8],
          '& .play-overlay': {
            opacity: 1,
          }
        }
      }}
      onClick={handleVideoClick}
    >
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          height="140"
          image={video.thumbnail}
          alt={video.title}
          sx={{ backgroundColor: 'grey.200' }}
        />
        {/* Play overlay */}
        <Box
          className="play-overlay"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0,
            transition: 'opacity 0.2s ease',
          }}
        >
          <PlayIcon sx={{ fontSize: 48, color: 'white' }} />
        </Box>
      </Box>
      
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" component="h3" sx={{ fontSize: '1rem', fontWeight: 600, lineHeight: 1.2 }}>
            {video.title}
          </Typography>
          <Tooltip title={isBookmarked ? 'Remove bookmark' : 'Bookmark video'}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onBookmark(video.id);
              }}
              sx={{ ml: 1, flexShrink: 0 }}
            >
              {isBookmarked ? (
                <BookmarkedIcon fontSize="small" color="primary" />
              ) : (
                <BookmarkIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
        
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ mb: 2, flexGrow: 1 }}
        >
          {video.description}
        </Typography>
        
        <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
          <Chip 
            label="Khan Academy" 
            size="small" 
            variant="outlined"
            sx={{ fontSize: '0.7rem' }}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

const LessonResources = ({ unitDay, lessonContent }) => {
  const theme = useTheme();
  const [bookmarkedVideos, setBookmarkedVideos] = useState(new Set());
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fromCache, setFromCache] = useState(false);

  // Load hardcoded Khan Academy videos for the unit day
  const fetchResources = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`🔍 Loading Khan Academy videos for Unit 5.0 - Day ${unitDay}`);
      
      // Use hardcoded Khan Academy videos
      const hardcodedVideos = getVerifiedKhanVideos(parseInt(unitDay));
      
      if (hardcodedVideos.length > 0) {
        setVideos(hardcodedVideos);
        setFromCache(false);
        console.log(`✅ Loaded ${hardcodedVideos.length} Khan Academy videos for Day ${unitDay}`);
      } else {
        console.log(`⚠️ No videos available for Unit Day ${unitDay}`);
        setVideos([]);
      }
    } catch (error) {
      console.error("Error loading videos:", error);
      setError("Unable to load videos. Please try again later.");
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };


  // Fetch resources when component mounts or unitDay changes
  useEffect(() => {
    if (lessonContent && lessonContent.trim()) {
      fetchResources();
    }
  }, [unitDay]);

  const handleBookmark = (videoId) => {
    setBookmarkedVideos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(videoId)) {
        newSet.delete(videoId);
      } else {
        newSet.add(videoId);
      }
      return newSet;
    });
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Learning Resources
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Khan Academy videos and additional resources for Unit 5.0 - Day {unitDay}
        </Typography>
      </Box>

      {/* Videos Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          📺 Khan Academy Videos
        </Typography>
        
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {!loading && !error && videos.length === 0 && (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No videos found for this lesson.
          </Typography>
        )}
        
        {!loading && videos.length > 0 && (
          <Grid container spacing={3}>
            {videos.map((video) => (
              <Grid item xs={12} sm={6} md={4} key={video.id}>
                <VideoCard
                  video={video}
                  onBookmark={handleBookmark}
                  isBookmarked={bookmarkedVideos.has(video.id)}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Additional Resources Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          📚 Additional Resources
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Card variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Practice Worksheets
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Printable worksheets for additional function practice
              </Typography>
              <Typography variant="body2" color="primary" sx={{ cursor: 'pointer' }}>
                Download PDF →
              </Typography>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Card variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Interactive Demos
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Online function graphing tools and calculators
              </Typography>
              <Typography variant="body2" color="primary" sx={{ cursor: 'pointer' }}>
                Open Tools →
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default LessonResources;